export const TaskViewMode = {
  MY_TASKS: 'myTasks',
  ALL_TASKS: 'allTasks',
  ARCHIVE: 'archive',
};

/**
 * Assignee ids that count as mine: the user, plus each team they belong to.
 *
 * @param {string|null|undefined} userId
 * @param {{ teamId?: string, userIds?: string[] }[]|null|undefined} teams
 * @returns {Set<string>}
 */
export function myAssigneeIds(userId, teams) {
  const ids = new Set();
  if (userId) ids.add(userId);
  for (const team of teams || []) {
    if (team?.teamId && team.userIds?.includes(userId)) {
      ids.add(team.teamId);
    }
  }
  return ids;
}

/**
 * Active tasks are incomplete. Archive is the completed ones. My Tasks is an
 * active task assigned to the user or one of their teams. Null means team
 * membership has not loaded and the assignee is not the user.
 *
 * @param {object|null|undefined} task
 * @param {string} viewMode
 * @param {{ userId?: string|null, teams?: { teamId?: string, userIds?: string[] }[], teamsReady?: boolean, hasTaskRead?: boolean }} context
 * @returns {boolean|null}
 */
export function taskBelongsInView(task, viewMode, context = {}) {
  if (!task) return false;

  const complete = task.isComplete === true;
  const mine = () => belongsToMe(task, complete, context);

  switch (viewMode) {
    case TaskViewMode.ARCHIVE:
      return complete;
    case TaskViewMode.ALL_TASKS:
      return context.hasTaskRead ? !complete : mine();
    case TaskViewMode.MY_TASKS:
    default:
      return mine();
  }
}

function belongsToMe(task, complete, context) {
  if (complete || !task.assigneeId) return false;
  if (task.assigneeId === context.userId) return true;
  if (!context.teamsReady) return null;
  return myAssigneeIds(context.userId, context.teams).has(task.assigneeId);
}

/**
 * @param {boolean} isTracked
 * @param {{ to?: string, type?: string }|null|undefined} transition
 * @param {string} viewMode
 * @param {boolean} hasTaskRead
 * @returns {boolean}
 */
export function shouldFetchTaskUpdate(isTracked, transition, viewMode, hasTaskRead) {
  return isTracked || transitionMayEnterTaskView(transition, viewMode, hasTaskRead);
}

/**
 * An unseen task. Completion is what moves one into the archive. An assignee
 * edit is what can move one into My Tasks. New tasks are created when a
 * document or consultation is filed, which is not a task event.
 *
 * @param {{ to?: string, type?: string }|null|undefined} transition
 * @param {string} viewMode
 * @param {boolean} hasTaskRead
 * @returns {boolean}
 */
export function transitionMayEnterTaskView(transition, viewMode, hasTaskRead) {
  if (!transition || transition.to === 'NON_EXISTENT') return false;

  const { type } = transition;
  if (type === 'COMPLETE') {
    return viewMode === TaskViewMode.ARCHIVE;
  }
  if (type === 'ASSIGNEE') {
    return viewMode === TaskViewMode.MY_TASKS
      || (viewMode === TaskViewMode.ALL_TASKS && !hasTaskRead);
  }
  return false;
}

/**
 * Task search matches a patient NHS number, a patient name, completed-by, and
 * created-by. Created-by is whoever filed the parent document or consultation,
 * so a new task arrives with that filing rather than a task event. Completion
 * is what makes completed-by start matching.
 *
 * @param {boolean} isInResults
 * @param {{ to?: string, type?: string }|null|undefined} transition
 * @returns {boolean}
 */
export function shouldRefreshTaskSearch(isInResults, transition) {
  if (!transition || transition.to === 'NON_EXISTENT') return false;
  return isInResults || transition.type === 'COMPLETE';
}

/**
 * @param {object[]|null|undefined} previous
 * @param {string} taskId
 * @param {object|null|undefined} fetched
 * @param {boolean|null} belongs
 * @returns {object[]}
 */
export function mergeFetchedTask(previous, taskId, fetched, belongs) {
  if (!Array.isArray(previous)) return previous;

  const position = previous.findIndex(task => task.taskId === taskId);
  const without = () => previous.filter(task => task.taskId !== taskId);

  if (fetched == null || belongs === false) {
    return position === -1 ? previous : without();
  }
  if (belongs == null) {
    if (position === -1) return previous;
    const next = previous.slice();
    next[position] = fetched;
    return next;
  }
  if (position === -1) return [...previous, fetched];
  const next = previous.slice();
  next[position] = fetched;
  return next;
}
